import { Component, OnInit, AfterViewChecked } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Dashboard } from '../model/dashboard.model';
import { CatalogService } from '../service/catalog.service';
import { MasterDataService } from '../service/master-data.service';
import { GraphDataService } from '../../service/graph-data.service';
import { AliasValidation } from '../common/alias-validation';
import { AliasStatus } from '../../model/graph-data.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ServiceTreeModalComponent } from '../service-tree/service-tree.component';
import { NgbModalErrorComponent } from '../../modal/ngb-modal-error.component';
import { NgbModalSuccessComponent } from '../../modal/ngb-modal-success.component';
import { NgbModalConfirmComponent } from '../../modal/ngb-modal-confirm.component';
import { ngbModalOptions } from '../../common/constants';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  standalone: false,
  selector: 'app-update-dashboard',
  templateUrl: './update-dashboard.component.html',
  styleUrls: ['./update-dashboard.component.css', '../../../style/common-styles.scss']
})
export class UpdateDashboardComponent implements OnInit , AfterViewChecked {

  dashboardID: number;
  typeList: string[];
  dataClassificationList: string[];
  accessMethodList: string[];
  dataSourceList: string[];
  alertList: boolean[];
  environmentList: string[];
  disableSecurityGroup = false;
  disableDataSourceValue = false;
  dashboard: Dashboard;
  isDataAvailable = false;
  isViewContentChecked = false;

  // for alias validation.
  aliasValidation: AliasValidation;
  isAliasInvalid: boolean;
  isValidationInProgress: boolean;
  invalidAliasMessage: string;

  constructor(private route: ActivatedRoute,
    private catalogService: CatalogService,
    private masterDataService: MasterDataService,
    private router: Router,
    graphDataService: GraphDataService,
    private modalService: NgbModal
  ) {
    this.route.params.subscribe(params => {
      this.dashboardID = params['dashboard-id'];
    });

    this.catalogService.getDashboardById(this.dashboardID).subscribe(
      (response) => {
        this.dashboard = response;
        this.dashboard.alerting = String(this.dashboard.alerting).charAt(0).toUpperCase() + String(this.dashboard.alerting).slice(1);
        if (this.dashboard.alerting === 'False') {
          this.disableSecurityGroup = true;
        }
        if (this.dashboard.dataSource === 'Unknown') {
          this.disableDataSourceValue = true;
        }
        this.isDataAvailable = true;
      }, (error) => {
        if (error.statusCode === 404) {
          const errorModalForUnauth = this.modalService.open(NgbModalErrorComponent);
          errorModalForUnauth.componentInstance.message = "Not Found";
          errorModalForUnauth.result.then(
            () => {
              this.router.navigate(['/monitoring-solution/browse-catalog']);
            }
          );
        } else if (error.statusCode === 403) {
          const errorModal = this.modalService.open(NgbModalErrorComponent);
          errorModal.componentInstance.message = "Access denied";
        } else {
          this.modalService.open(NgbModalErrorComponent);
        }
      }
    );

    this.masterDataService.getComboBoxData().subscribe(
      (response) => {
        this.accessMethodList = response.accessMethod;
        this.typeList = response.type;
        this.alertList = response.alerts;
        this.dataClassificationList = response.dataClassification;
        this.dataSourceList = response.dataSource;
        this.environmentList = response.environment;
      },
      (error) => {
        const errorModal = this.modalService.open(NgbModalErrorComponent);
        if (error.statusCode === 403) {
          errorModal.componentInstance.message = "Access denied";
        }
      }
    );

    // for verifying aliases.
    this.aliasValidation = new AliasValidation(graphDataService);
    this.isAliasInvalid = false;
    this.isValidationInProgress = false;
  }

  open() {
    this.modalService.open(ServiceTreeModalComponent, { centered: true }).result.then((node) => {
      this.dashboard.serviceTree = node.name + "(" + node.id + ")";
    }, () => { });
  }

  ngOnInit() {  }

  ngAfterViewChecked() {
    const focusElement: HTMLElement = document.getElementById('name') as HTMLElement;
    if (!this.isViewContentChecked && focusElement != null) {
      focusElement.focus();
      this.isViewContentChecked = true;
    }
  }

  onSubmit() {
    this.dashboard.alerting = this.dashboard.alerting.toLowerCase() === 'true' ? true : false;
    if (!this.dashboard.alerting) {
      this.dashboard.securityGroup = "";
    }
    if (this.dashboard.dataSource === "Unknown") {
      this.dashboard.dataSourceValue = "";
    }
    this.dashboard.owners = this.dashboard.owners.replace(/ /g, "");
    this.catalogService.updateDashboard(this.dashboard).subscribe(
      (response) => {
        if (response.status === 204) {
          const successModal = this.modalService.open(NgbModalSuccessComponent, ngbModalOptions);
          successModal.componentInstance.message = "Successfully updated Dashboard";
          successModal.result.then(
            () => {
              this.router.navigate(['/monitoring-solution/browse-catalog']);
            }
          );
        } else {
          const errorModal = this.modalService.open(NgbModalErrorComponent);
          errorModal.componentInstance.message = "Error updating Dashboard";
        }
      },
      (error: HttpErrorResponse) => {
        const errorModal = this.modalService.open(NgbModalErrorComponent);
        if (error.status === 403) {
          errorModal.componentInstance.message = "Access denied";
        }
      }
    );
  }

  disableDataSourceValueInput(value: string) {
    if (value === 'Unknown') {
      this.disableDataSourceValue = true;
    } else {
      this.disableDataSourceValue = false;
    }
  }

  disableSecurityGroupInput(value: string) {
    if (value === 'False') {
      this.disableSecurityGroup = true;
    } else {
      this.disableSecurityGroup = false;
    }
  }

  checkAlias(aliases: string) {
    if (aliases.trim() === ';' || aliases.trim() === '') {
      this.isAliasInvalid = true;
      this.invalidAliasMessage = 'Please enter a valid alias';
      return "";
    }
    this.isAliasInvalid = false;
    this.isValidationInProgress = true;
    let invalidAlias: Array<AliasStatus>;
    this.aliasValidation.checkAlias(aliases).subscribe(
      (response) => {
        invalidAlias = response;
        invalidAlias = invalidAlias.filter(alias => alias.isValid === false);

        // display the alert message if invalidAlias has values.
        if (invalidAlias.length !== 0) {
          this.isAliasInvalid = true;
          this.invalidAliasMessage = 'Invalid Alias: ';
          for (let index = 0; index < invalidAlias.length; index++) {
            this.invalidAliasMessage += invalidAlias[index].alias;
            if (index === (invalidAlias.length - 1)) {
              this.invalidAliasMessage += '.';
            } else {
              this.invalidAliasMessage += ', ';
            }
          }
        } else {
          this.isAliasInvalid = false;
        }
      },
      (error) => {
        this.modalService.open(NgbModalErrorComponent);
      },
      () => {
        this.isValidationInProgress = false;
      }
    );
  }

  cancelClick(): void {
    this.router.navigate(['monitoring-solution/browse-catalog']);
  }

  delete() {
    const modal = this.modalService.open(NgbModalConfirmComponent);
    modal.componentInstance.message = "Are you sure you want to delete?";
    modal.result.then(() => {
      this.catalogService.deleteDashboard(this.dashboard).subscribe(
        (response) => {
          const successModal = this.modalService.open(NgbModalSuccessComponent);
          successModal.componentInstance.message = "Successfully deleted Dashboard";
          successModal.result.then(
            () => {
              this.router.navigate(['/monitoring-solution/browse-catalog']);
            }
          );
        }, 
        (error: HttpErrorResponse) => {
          const errorModal = this.modalService.open(NgbModalErrorComponent);
          if (error.status === 403) {
            errorModal.componentInstance.message = "Access denied";
          }
        });
    }, () => { });
  }

  checkAliasAfterSubmit(aliases: string) {
    if (aliases.trim() === ';' || aliases.trim() === '') {
      this.isAliasInvalid = true;
      this.invalidAliasMessage = 'Please enter a valid alias';
      return "";
    }
    this.isAliasInvalid = false;
    this.isValidationInProgress = true;
    let invalidAlias: Array<AliasStatus>;
    this.aliasValidation.checkAlias(aliases).subscribe(
      (response) => {
        invalidAlias = response;
        invalidAlias = invalidAlias.filter(alias => alias.isValid === false);

        // display the alert message if invalidAlias has values.
        if (invalidAlias.length !== 0) {
          this.isAliasInvalid = true;
          this.invalidAliasMessage = 'Invalid Alias: ';
          for (let index = 0; index < invalidAlias.length; index++) {
            this.invalidAliasMessage += invalidAlias[index].alias;
            if (index === (invalidAlias.length - 1)) {
              this.invalidAliasMessage += '.';
            } else {
              this.invalidAliasMessage += ', ';
            }
          }
        } else {
          this.isAliasInvalid = false;
        }
      },
      (error) => {
        this.modalService.open(NgbModalErrorComponent);
      },
      () => {
        this.isValidationInProgress = false;
        if (!this.isAliasInvalid) {
          this.onSubmit();
        }
      }
    );
  }
}

